import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo, useRef, useState } from "react"
import { useCurrencyFormat } from "@/hooks/use-currency-format"
import { useDateFormat } from "@/hooks/use-date-format"
import { useGoals } from "@/hooks/use-goals"
import { api } from "@/lib/api"
import { cn } from "@/lib/cn"
import { type PendingAction, useUIStore } from "@/store/ui"

// ─── Static pages ────────────────────────────────────────────
const PAGES = [
  { label: "Dashboard", to: "/", group: "Pages", hint: "Overview" },
  { label: "Transactions", to: "/transactions", group: "Pages", hint: "Browse & filter" },
  { label: "Analytics", to: "/analytics", group: "Pages", hint: "Spending trends" },
  { label: "Budgets", to: "/budgets", group: "Pages", hint: "Actual vs planned" },
  { label: "Upcoming", to: "/upcoming", group: "Pages", hint: "Bills & EMIs due" },
  { label: "Subscriptions", to: "/subscriptions", group: "Pages", hint: "Recurring payments" },
  { label: "Net Worth", to: "/net-worth", group: "Pages", hint: "Snapshots" },
  { label: "Investments", to: "/investments", group: "Pages", hint: "Portfolio summary" },
  { label: "Goals", to: "/goals", group: "Pages", hint: "Sinking funds & EMIs" },
  { label: "Insurance", to: "/insurance", group: "Pages", hint: "Policies" },
  { label: "Reports", to: "/reports", group: "Pages", hint: "Monthly breakdown" },
  { label: "Heatmap", to: "/heatmap", group: "Pages", hint: "Daily spend calendar" },
  { label: "Tax", to: "/tax", group: "Pages", hint: "Estimate, capital gains, 80C" },
  { label: "Calculators", to: "/calculators", group: "Pages", hint: "SIP, FIRE, Tax, EMI…" },
  { label: "Settings", to: "/settings", group: "Pages", hint: "Accounts & merchants" },
]

type ResultItem = {
  id: string
  label: string
  sublabel?: string
  hint?: string
  group: string
  to: string
  search?: string
  action?: PendingAction
}

const ACTIONS: ResultItem[] = [
  {
    id: "action:add_transaction",
    label: "Add transaction",
    hint: "Log a spend or income by hand",
    group: "Actions",
    to: "/transactions",
    action: "add_transaction",
  },
  {
    id: "action:csv_import",
    label: "Import CSV",
    hint: "A CSV file from any bank",
    group: "Actions",
    to: "/transactions",
    action: "csv_import",
  },
  {
    id: "action:new_goal",
    label: "New Goal",
    hint: "Create savings goal",
    group: "Actions",
    to: "/goals",
    action: "new_goal",
  },
  {
    id: "action:log_contribution",
    label: "Log Contribution",
    hint: "Add to a goal",
    group: "Actions",
    to: "/goals",
    action: "log_contribution",
  },
  {
    id: "action:add_fd",
    label: "Add FD",
    hint: "New fixed deposit",
    group: "Actions",
    to: "/investments",
    action: "add_fd",
  },
  {
    id: "action:add_debt",
    label: "Add Debt",
    hint: "Track a loan or liability",
    group: "Actions",
    to: "/debt",
    action: "add_debt",
  },
  {
    id: "action:export_transactions",
    label: "Export Transactions",
    hint: "Download as CSV",
    group: "Actions",
    to: "/transactions",
    action: "export_transactions",
  },
  {
    id: "action:set_budget",
    label: "Set Budget",
    hint: "Manage category budgets",
    group: "Actions",
    to: "/budgets",
  },
]

function useSearchResults(query: string): { items: ResultItem[]; isLoading: boolean } {
  const fmt = useCurrencyFormat()
  const formatDate = useDateFormat()
  const trimmed = query.trim().toLowerCase()

  // Filter pages instantly
  const pageResults: ResultItem[] = useMemo(
    () =>
      PAGES.filter(
        (p) => p.label.toLowerCase().includes(trimmed) || p.hint?.toLowerCase().includes(trimmed),
      ).map((p) => ({ id: `page:${p.to}`, ...p })),
    [trimmed],
  )

  // Transaction search — only when query is non-empty
  const { data: txnData, isFetching } = useQuery({
    queryKey: ["cmd-search-txn", trimmed],
    queryFn: () =>
      api.get<{
        data: Array<{
          id: string
          date: string
          description: string
          amount: string
          type: string
          merchant?: { cleanName: string } | null
        }>
      }>(`/transactions?search=${encodeURIComponent(trimmed)}&pageSize=5`),
    enabled: trimmed.length >= 2,
    staleTime: 30 * 1000,
  })

  // Goals search — filter from cached goals
  const { data: goalsData } = useGoals()

  const goalResults: ResultItem[] = useMemo(() => {
    if (!trimmed) return []
    const allGoals = [
      ...(goalsData?.goals ?? []).map((g) => ({
        id: `goal:${g.id}`,
        label: g.name,
        // Household-aware currency, not a hardcoded ₹/en-IN literal (#72).
        sublabel: `Goal · ${fmt(Number(g.targetAmount))} target`,
        group: "Goals",
        to: "/goals",
      })),
      ...(goalsData?.emis ?? []).map((e) => ({
        id: `emi:${e.id}`,
        label: e.name,
        sublabel: `EMI · ${fmt(Number(e.emiAmount))}/mo`,
        group: "Goals",
        to: "/goals",
      })),
    ]
    return allGoals.filter((g) => g.label.toLowerCase().includes(trimmed))
  }, [goalsData, trimmed, fmt])

  const txnResults: ResultItem[] = useMemo(() => {
    if (!txnData?.data?.length) return []
    return txnData.data.map((t) => ({
      id: `txn:${t.id}`,
      label: t.merchant?.cleanName || t.description.slice(0, 50),
      sublabel: `${formatDate(t.date)} · ${t.type === "debit" ? "-" : "+"}${fmt(Math.abs(parseFloat(t.amount)))}`,
      group: "Transactions",
      to: "/transactions",
      search: t.merchant?.cleanName || t.description.slice(0, 50),
    }))
  }, [txnData, fmt, formatDate])

  const actionResults = useMemo(
    () => (trimmed ? ACTIONS.filter((a) => a.label.toLowerCase().includes(trimmed)) : ACTIONS),
    [trimmed],
  )

  const items = trimmed
    ? [...actionResults, ...pageResults, ...goalResults, ...txnResults]
    : [...ACTIONS, ...PAGES.slice(0, 8).map((p) => ({ id: `page:${p.to}`, ...p }))]

  return { items, isLoading: isFetching }
}

export function CommandPalette() {
  const open = useUIStore((s) => s.commandPaletteOpen)
  const close = useUIStore((s) => s.closeCommandPalette)
  const setPendingAction = useUIStore((s) => s.setPendingAction)
  const navigate = useNavigate()
  const [query, setQuery] = useState("")
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const { items, isLoading } = useSearchResults(query)

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setQuery("")
      setActiveIndex(0)
      setTimeout(() => inputRef.current?.focus(), 10)
    }
  }, [open])

  // Keep active item in view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${activeIndex}"]`) as HTMLElement | null
    el?.scrollIntoView({ block: "nearest" })
  }, [activeIndex])

  function select(item: ResultItem) {
    close()
    if (item.action) {
      setPendingAction(item.action)
      navigate({ to: item.to as "/" })
    } else if (item.search) {
      navigate({
        to: "/transactions",
        search: { page: 1, search: item.search, categoryType: "", owner: "" },
      })
    } else {
      navigate({ to: item.to as "/" })
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, items.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (items[activeIndex]) select(items[activeIndex])
    } else if (e.key === "Escape") {
      close()
    }
  }

  if (!open) return null

  // Group items for rendering
  const groups: Record<string, ResultItem[]> = {}
  for (const item of items) {
    if (!groups[item.group]) groups[item.group] = []
    groups[item.group].push(item)
  }

  let globalIndex = 0

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
      onClick={close}
      onKeyDown={() => {}}
    >
      {/* Palette */}
      <div
        className="mx-auto mt-[15vh] w-full max-w-[560px] overflow-hidden rounded-[14px] border border-border-default bg-bg-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Input */}
        <div className="flex items-center gap-3 border-b border-border-default px-4">
          <svg
            aria-hidden="true"
            className="h-4 w-4 shrink-0 text-text-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
            />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setActiveIndex(0)
            }}
            placeholder="Search pages, transactions, goals…"
            aria-label="Search pages, transactions, and goals"
            className="flex-1 bg-transparent py-3.5 text-[14px] text-text-primary outline-none placeholder:text-text-hint"
          />
          {isLoading && (
            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-text-muted border-t-transparent" />
          )}
          <kbd className="rounded bg-bg-elevated px-1.5 py-0.5 text-[10px] text-text-muted">
            esc
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[400px] overflow-y-auto py-2">
          {items.length === 0 && !isLoading && (
            <p className="px-4 py-6 text-center text-[13px] text-text-muted">No results</p>
          )}

          {Object.entries(groups).map(([group, groupItems]) => (
            <div key={group}>
              <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-text-muted">
                {group}
              </p>
              {groupItems.map((item) => {
                const idx = globalIndex++
                const isActive = idx === activeIndex
                return (
                  <button
                    key={item.id}
                    type="button"
                    data-index={idx}
                    onClick={() => select(item)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-2 text-left transition-colors",
                      isActive ? "bg-bg-elevated" : "hover:bg-bg-elevated",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] text-text-primary">{item.label}</p>
                      {item.sublabel && (
                        <p className="truncate text-[11px] text-text-muted">{item.sublabel}</p>
                      )}
                    </div>
                    {item.hint && (
                      <span className="shrink-0 text-[11px] text-text-hint">{item.hint}</span>
                    )}
                  </button>
                )
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 border-t border-border-default px-4 py-2 text-[10px] text-text-hint">
          <span>
            <kbd className="mr-1 rounded bg-bg-elevated px-1 py-0.5">↑↓</kbd>navigate
          </span>
          <span>
            <kbd className="mr-1 rounded bg-bg-elevated px-1 py-0.5">↵</kbd>open
          </span>
          <span>
            <kbd className="mr-1 rounded bg-bg-elevated px-1 py-0.5">esc</kbd>close
          </span>
        </div>
      </div>
    </div>
  )
}
